<script setup lang="ts">
import {
  useBillingStore,
  type BillingTermKind,
  type SimulationQuote,
} from '~/store/billing'
import type { CheckoutTrialSummary } from '~/types/checkout-trial'

const props = defineProps<{
  trial?: CheckoutTrialSummary | null
  quote: SimulationQuote | null
  simulating?: boolean
  simulationError?: string | null
  isNewSubscription: boolean
  prorataEstimate?: string | null
  prorataHours?: number | null
  canPlaceOrder: boolean
  placing?: boolean
  ctaLabel: string
  ctaIcon?: string
  blockers: { key: string; label: string }[]
  /** Enables the per-month headline (see CheckoutTotals). Optional. */
  term?: BillingTermKind
  onDemandMonthlyMicroCents?: string | null
  initialCreditCents?: string | null
}>()
const emit = defineEmits<{ 'place-order': [] }>()

/**
 * Confirming from inside the summary drawer.
 *
 * The drawer has to close FIRST: whatever `place-order` does happens on the
 * page behind it — a scroll to the next step, or a payment panel — and neither
 * is visible under an open sheet, which is the same "I clicked and nothing
 * happened" this button exists to avoid.
 */
function confirmFromSummary() {
  if (!props.canPlaceOrder) return
  summaryOpen.value = false
  emit('place-order')
}

const billing = useBillingStore()
const pricing = useCheckoutPricing()
const summaryOpen = ref(false)

/** Prepaid credit for new on-demand orders; monthly price for other orders. */
const totalLabel = computed(() => {
  if (props.trial) return billing.formatCents('0')
  if (!props.quote) return null
  if (props.initialCreditCents) return billing.formatCents(props.initialCreditCents)
  const monthly = pricing.perMonth(props.quote.total_hourly_rate_micro_cents)
  if (monthly != null) return pricing.fmt(monthly)
  return `${billing.formatMicroCents(props.quote.total_hourly_rate_micro_cents)}/h`
})
</script>

<template>
  <!-- pr-20 keeps the CTA clear of the floating assistant button -->
  <div
    class="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-default bg-default/95 backdrop-blur pl-4 pr-20 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
  >
    <div class="flex items-center justify-between gap-3">
      <UDrawer
        v-model:open="summaryOpen"
        :title="$t('billing.page.checkout.summary.title')"
      >
        <button type="button" class="text-left min-w-0">
          <div class="text-base font-semibold truncate">
            <template v-if="totalLabel">
              {{ totalLabel }}<span v-if="!initialCreditCents && !trial" class="text-xs font-normal text-muted">{{
                $t('billing.page.checkout.term.perMonth')
              }}</span>
            </template>
            <template v-else>—</template>
          </div>
          <div class="text-xs text-primary flex items-center gap-1">
            {{ initialCreditCents
              ? $t('billing.page.checkout.recharge.title')
              : $t('billing.page.checkout.mobile.viewSummary') }}
            <UIcon name="i-lucide-chevron-up" class="size-3.5" />
          </div>
        </button>

        <template #body>
          <div class="space-y-3 text-sm">
            <CheckoutTotals
              :trial="trial"
              :quote="quote"
              :simulating="simulating"
              :simulation-error="simulationError"
              :is-new-subscription="isNewSubscription"
              :prorata-estimate="prorataEstimate"
              :prorata-hours="prorataHours"
              :term="term"
              :on-demand-monthly-micro-cents="onDemandMonthlyMicroCents"
              :initial-credit-cents="initialCreditCents"
            />
            <CheckoutOrderBlockers id="mobile-summary-blockers" :blockers="blockers" />
            <CheckoutCurrencyNote />

            <!-- The same action, inside the summary.
                 Reviewing the order is exactly the moment somebody decides to
                 go ahead, and this panel used to end there: no button at all,
                 so the customer had to dismiss the drawer and find the bar
                 again. It closes the drawer first, because the action it
                 triggers happens on the page BEHIND it. -->
            <UButton
              block
              size="lg"
              color="primary"
              class="justify-center"
              :loading="placing"
              :aria-disabled="!canPlaceOrder"
              :aria-describedby="canPlaceOrder ? undefined : 'mobile-summary-blockers'"
              :icon="ctaIcon"
              @click="confirmFromSummary"
            >
              {{ ctaLabel }}
            </UButton>
          </div>
        </template>
      </UDrawer>

      <!-- `aria-disabled`, not `disabled`: a disabled button leaves the tab
           order and announces nothing, so the one control the customer came for
           becomes unreachable and unexplained. Same call as the sidebar CTA. -->
      <UButton
        size="lg"
        color="primary"
        class="shrink-0"
        :loading="placing"
        :aria-disabled="!canPlaceOrder"
        :icon="ctaIcon"
        @click="canPlaceOrder && emit('place-order')"
      >
        {{ ctaLabel }}
      </UButton>
    </div>
  </div>
</template>
